import type { MouseEvent } from 'react'

/** A document the agent produced (or the user attached) shown beside the chat. */
export interface PreviewDocument {
  documentId: string
  /** Picks one of a multi-attachment document; the first attachment without it. */
  attachmentId?: string | null
  fileName?: string | null
}

/** Identity of a preview — two chips on one multi-attachment document are distinct. */
export function previewDocumentKey(doc: PreviewDocument): string {
  return doc.attachmentId ? `${doc.documentId}:${doc.attachmentId}` : doc.documentId
}

const PREVIEWABLE_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'])
const TABLE_EXTENSIONS = new Set(['csv', 'tsv', 'xlsx', 'xls'])

function documentUrl(doc: PreviewDocument, path: string): string {
  const base = `/api/v1/insure/internal/documents/${doc.documentId}/${path}/`
  return doc.attachmentId ? `${base}?attachment_id=${doc.attachmentId}` : base
}

function extension(fileName: string | null | undefined): string {
  return (fileName ?? '').toLowerCase().split('.').pop() ?? ''
}

/** Same-origin bytes for a document — a signed storage URL has no CORS for us. */
export function documentContentUrl(doc: PreviewDocument): string {
  return documentUrl(doc, 'content')
}

/** A spreadsheet's cells as JSON, which the backend reads with the import's own parsers. */
export function documentTableUrl(doc: PreviewDocument): string {
  return documentUrl(doc, 'table')
}

/** Whether the browser can render this inline; everything else stays a download. */
export function isPreviewable(fileName: string | null | undefined): boolean {
  return PREVIEWABLE_EXTENSIONS.has(extension(fileName))
}

/** A spreadsheet the browser can't show, but the backend can turn into a table. */
export function isTablePreviewable(fileName: string | null | undefined): boolean {
  return TABLE_EXTENSIONS.has(extension(fileName))
}

/** A plain click on a file the preview can show; a modified or non-primary click is the browser's: new tab, new window, download. */
export function opensInPreview(event: MouseEvent, fileName: string | null | undefined): boolean {
  const browserHandles =
    event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  return !browserHandles && (isPreviewable(fileName) || isTablePreviewable(fileName))
}
